import React from 'react'
import { useMutation, useQuery } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { useClient } from '@hooks/useClient'
import { useAlert } from '@hooks/useAlert'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { queryClient } from '@client/init'
import { CampaignTypeEnum } from '@interfaces/promotionCampaign/CampaignTypeEnum'
import { CampaignUnavailableItemType } from '@models/promotionCampaign/CampaignUnavailableItemType'
import { UnavailableItemsDialog } from '../../components/UnavailableItemsDialog/UnavailableItemsDialog'
import { CampaignDetailPageProps } from './interface'

export function withCampaignDetailPage(
  Component: React.FC<CampaignDetailPageProps>
) {
  function WithCampaignDetailPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const client = useClient()
    const alert = useAlert()
    const confirmModal = useModal({ modal: 'confirm' })

    const { data: detail, isLoading } = useQuery(
      ['campaign-detail', Number(id)],
      () => client?.promotionCampaignClient.getCampaignDetail(Number(id)),
      {
        onError: () => navigate('/promotion-campaign'),
      }
    )

    const { mutate: approveCampaign } = useMutation(
      () => client!.promotionCampaignClient.approveCampaign(Number(id)),
      {
        onSuccess: () => {
          confirmModal.hide()
          alert.success('อนุมัติแคมเปญสำเร็จ')
          queryClient.invalidateQueries(['campaign-detail', Number(id)])
        },
        onError: (error: any) => {
          const graphQLError = error?.response?.errors?.[0]
          const unavailableItems: CampaignUnavailableItemType[] | undefined =
            graphQLError?.extensions?.extras?.unavailableItems
          if (unavailableItems?.length) {
            confirmModal.show({
              content: <UnavailableItemsDialog items={unavailableItems} />,
              hideConfirmButton: true,
              hideCloseButton: true,
            })
            return
          }
          confirmModal.hide()
          alert.error(
            graphQLError?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่ภายหลัง',
            { autoClose: false }
          )
        },
      }
    )

    const { mutateAsync: unapproveCampaign } = useMutation(
      () => client!.promotionCampaignClient.unapproveCampaign(Number(id)),
      {
        onSuccess: () => {
          alert.success('ยกเลิกการอนุมัติแคมเปญสำเร็จ')
          queryClient.invalidateQueries(['campaign-detail', Number(id)])
        },
        onError: (error: any) => {
          alert.error(
            error?.response?.errors?.[0]?.message ||
              'เกิดข้อผิดพลาด กรุณาลองใหม่ภายหลัง',
            { autoClose: false }
          )
        },
      }
    )

    const { mutateAsync: rejectCampaign } = useMutation(
      () => client!.promotionCampaignClient.rejectCampaign(Number(id)),
      {
        onSuccess: () => {
          alert.success('ปฏิเสธแคมเปญสำเร็จ')
          queryClient.invalidateQueries(['campaign-detail', Number(id)])
        },
        onError: () => {
          alert.error('เกิดข้อผิดพลาด กรุณาลองใหม่ภายหลัง')
        },
      }
    )

    const { mutateAsync: deleteCampaign } = useMutation(
      () => client!.promotionCampaignClient.deleteCampaign(Number(id)),
      {
        onSuccess: () => {
          alert.success('ลบแคมเปญสำเร็จ')
          navigate('/promotion-campaign')
        },
        onError: () => {
          alert.error('เกิดข้อผิดพลาด กรุณาลองใหม่ภายหลัง')
        },
      }
    )

    function handleApprove() {
      confirmModal.show({
        content: (
          <div className='w-[280px]'>
            <p className='font-mitr text-center text-[24px] text-secondary font-medium'>
              อนุมัติแคมเปญ
            </p>
            <div className='flex flex-col text-center mt-[20px] mb-[30px] text-[14px] font-light text-secondary'>
              คุณกำลังจะอนุมัติแคมเปญ
              <span className='font-bold'>&ldquo;{detail?.name}&rdquo;</span>
              ต้องการที่จะดำเนินการต่อหรือไม่
            </div>
          </div>
        ),
        onConfirm: () => approveCampaign(),
        onClose: () => confirmModal.hide(),
        confirmButtonText: 'ยืนยัน',
      })
    }

    function handleUnapprove() {
      confirmModal.show({
        content: (
          <div className='w-[280px]'>
            <p className='font-mitr text-center text-[24px] text-secondary font-medium'>
              ยกเลิกการอนุมัติ
            </p>
            <div className='flex flex-col text-center mt-[20px] mb-[30px] text-[14px] font-light text-secondary'>
              คุณกำลังจะยกเลิกการอนุมัติแคมเปญ
              <span className='font-bold'>&ldquo;{detail?.name}&rdquo;</span>
              ส่วนลดของหนังสือในแคมเปญนี้จะถูกนำออก และแคมเปญจะกลับเป็นแบบร่าง
              ต้องการที่จะดำเนินการต่อหรือไม่
            </div>
          </div>
        ),
        onConfirm: async () => {
          await unapproveCampaign()
          confirmModal.hide()
        },
        onClose: () => confirmModal.hide(),
        confirmButtonText: 'ยืนยัน',
      })
    }

    function handleReject() {
      confirmModal.show({
        content: (
          <div className='w-[280px]'>
            <p className='font-mitr text-center text-[24px] text-secondary font-medium'>
              ปฏิเสธแคมเปญ
            </p>
            <div className='flex flex-col text-center mt-[20px] mb-[30px] text-[14px] font-light text-secondary'>
              คุณกำลังจะปฏิเสธแคมเปญ
              <span className='font-bold'>&ldquo;{detail?.name}&rdquo;</span>
              ต้องการที่จะดำเนินการต่อหรือไม่
            </div>
          </div>
        ),
        onConfirm: async () => {
          await rejectCampaign()
          confirmModal.hide()
        },
        onClose: () => confirmModal.hide(),
        confirmButtonText: 'ยืนยัน',
      })
    }

    function handleDelete() {
      confirmModal.show({
        content: (
          <div className='w-[280px]'>
            <p className='font-mitr text-center text-[24px] text-secondary font-medium'>
              ลบแคมเปญ
            </p>
            <div className='flex flex-col text-center mt-[20px] mb-[30px] text-[14px] font-light text-secondary'>
              คุณกำลังจะลบแคมเปญ
              <span className='font-bold'>&ldquo;{detail?.name}&rdquo;</span>
              ต้องการที่จะดำเนินการต่อหรือไม่
            </div>
          </div>
        ),
        onConfirm: async () => {
          await deleteCampaign()
          confirmModal.hide()
        },
        onClose: () => confirmModal.hide(),
        confirmButtonText: 'ยืนยัน',
      })
    }

    const componentProps: CampaignDetailPageProps = {
      detail,
      bookData: detail?.items || [],
      campaignType: detail?.type || CampaignTypeEnum.PER_CHAPTER,
      wholeStoryDiscountType: detail?.wholeStoryDiscountType,
      freeReadingType: detail?.freeReadingType,
      isLoading,
      handleApprove,
      handleUnapprove,
      handleReject,
      handleDelete,
    }

    return <Component {...componentProps} />
  }

  return WithCampaignDetailPage
}
